import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Dropdown, Menu, Modal, Tag, Tooltip } from 'antd';
import {
  ApartmentOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';

import { useClient } from 'src/contexts';
import { computeCustomerTreeStats } from './customerTreeStats';
import { getCustomerTypeConfig } from './customerTypeLabels';
import { CustomerTableDataType } from './types';

import styles from './Customers.module.css';

const { confirm } = Modal;

type CustomerTileProps = {
  customer: CustomerTableDataType;
  onStatusChange: (id: string, active: boolean) => void;
  onDelete: (id: string) => void;
};

const CustomerTile: React.FC<CustomerTileProps> = ({
  customer,
  onStatusChange,
  onDelete,
}) => {
  const navigate = useNavigate();
  const client = useClient();
  const [networksCount, setNetworksCount] = useState<number | null>(null);
  const [usersCount, setUsersCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Un seul appel : réseaux + utilisateurs dérivés de l'arbre des groupes.
    client.get(
      `/customers/${customer.id}/groups?per_page=100&page=1`,
      (response: any) => {
        if (cancelled) return;
        const stats = computeCustomerTreeStats(response?.data ?? []);
        setNetworksCount(stats.networks);
        setUsersCount(stats.users);
      },
      () => {
        if (cancelled) return;
        setNetworksCount(0);
        setUsersCount(0);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [customer.id, client]);

  const typeConfig = getCustomerTypeConfig(customer.type);
  const isActive = customer.status === 'active';

  const onCardClick = () => navigate(customer.id);

  const onToggleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    confirm({
      title: `${isActive ? 'Désactiver' : 'Activer'} ${customer.name} ?`,
      icon: <ExclamationCircleOutlined />,
      content: isActive
        ? "Les gestionnaires de ce client perdront l'accès à Pilotage. Les données restent en place."
        : "Les gestionnaires retrouveront l'accès à Pilotage.",
      okText: isActive ? 'Désactiver' : 'Activer',
      okType: isActive ? 'danger' : 'primary',
      cancelText: 'Annuler',
      onOk() {
        onStatusChange(customer.id, !isActive);
      },
    });
  };

  const onDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    confirm({
      title: `Supprimer ${customer.name} ?`,
      icon: <ExclamationCircleOutlined />,
      content:
        'Cette action est irréversible. Toutes les données du client (réseaux, communautés, etc.) seront supprimées.',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk() {
        onDelete(customer.id);
      },
    });
  };

  const overlayMenu = (
    <Menu
      items={[
        {
          key: 'toggle',
          icon: isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />,
          label: isActive ? 'Désactiver' : 'Activer',
          onClick: ({ domEvent }: any) => onToggleStatusClick(domEvent),
        },
        { type: 'divider' as const },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          danger: true,
          label: 'Supprimer',
          onClick: ({ domEvent }: any) => onDeleteClick(domEvent),
        },
      ]}
    />
  );

  return (
    <div className={styles.tile} onClick={onCardClick}>
      <div className={styles.tileHeader}>
        <Avatar
          icon={<ApartmentOutlined />}
          size={44}
          className={styles.tileAvatar}
          style={{ backgroundColor: '#6366f1' }}
        />
        <div className={styles.tileIdentity}>
          <Tooltip title={customer.name}>
            <span className={styles.tileName}>{customer.name}</span>
          </Tooltip>
          <div className={styles.tileBadges}>
            <Tag color={typeConfig.color}>{typeConfig.label}</Tag>
            {isActive ? (
              <span className={styles.statusActive}>
                <span
                  className={`${styles.statusDot} ${styles.statusDotActive}`}
                />
                Actif
              </span>
            ) : (
              <span className={styles.statusInactive}>
                <span
                  className={`${styles.statusDot} ${styles.statusDotInactive}`}
                />
                Inactif
              </span>
            )}
          </div>
        </div>
        <Dropdown
          overlay={overlayMenu}
          trigger={['click']}
          placement="bottomRight"
        >
          <button
            className={styles.tileMenu}
            onClick={e => e.stopPropagation()}
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.statBlock}>
          {networksCount === null ? (
            <span className={styles.statValueLoading}>…</span>
          ) : (
            <span className={styles.statValue}>{networksCount}</span>
          )}
          <span className={styles.statLabel}>
            Réseau{networksCount && networksCount > 1 ? 'x' : ''}
          </span>
        </div>
        <div className={styles.statBlock}>
          {usersCount === null ? (
            <span className={styles.statValueLoading}>…</span>
          ) : (
            <span className={styles.statValue}>
              {usersCount.toLocaleString('fr-FR')}
            </span>
          )}
          <span className={styles.statLabel}>
            Utilisateur{usersCount && usersCount > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className={styles.tileFooter}>
        <span>Créé le {customer.createdAt}</span>
      </div>
    </div>
  );
};

export default CustomerTile;
